/*
 * wishlist.js — lightweight, dependency-free wishlist.
 *
 * PER-DEVICE LIMITATION
 * ---------------------
 * The wishlist is stored in `localStorage` under the key `theme:wishlist`
 * (a JSON array of product handles). localStorage is scoped to one browser
 * on one device, so:
 *   - the list is NOT synced to the customer account,
 *   - it does NOT follow the shopper to another device or browser,
 *   - it is lost if the shopper clears site data, and it is unavailable in
 *     browsers that block storage (e.g. Safari private mode), where this
 *     script silently falls back to an in-memory list for the session only.
 * The wishlist page surfaces this to the shopper via `custom.wishlist.device_note`.
 *
 * Public API (see BUILD-SPEC):
 *   window.Wishlist.get()          -> Array<string> of product handles
 *   window.Wishlist.has(handle)    -> Boolean
 *   window.Wishlist.toggle(handle) -> Boolean (the new state)
 *   window.Wishlist.count()        -> Number
 *
 * On every change it dispatches:
 *   document.dispatchEvent(new CustomEvent('wishlist:change', { detail: { count } }))
 *
 * Hearts are bound with event delegation on `document`, so cards injected
 * later (Load More, AJAX filtering, quick view) work with no extra wiring:
 *   <button class="wishlist-toggle" data-product-handle="..." aria-pressed="false">
 */
(function () {
  'use strict';

  /* Idempotent: the asset may be referenced both globally and by a section. */
  if (window.Wishlist && window.Wishlist.__initialized) return;

  var STORAGE_KEY = 'theme:wishlist';
  var TOGGLE_SELECTOR = '.wishlist-toggle';
  var COUNT_SELECTOR = '[data-wishlist-count]';

  /* In-memory mirror. Used as the source of truth when localStorage is
     unavailable (Safari private mode, storage disabled, quota errors). */
  var memoryStore = [];
  var storageAvailable = true;

  function sanitize(list) {
    var clean = [];
    if (!list || typeof list.length !== 'number') return clean;

    for (var i = 0; i < list.length; i++) {
      var handle = list[i];
      if (typeof handle === 'string') {
        handle = handle.trim();
        if (handle !== '' && clean.indexOf(handle) === -1) clean.push(handle);
      }
    }

    return clean;
  }

  function readStorage() {
    if (!storageAvailable) return memoryStore.slice();

    try {
      var raw = window.localStorage.getItem(STORAGE_KEY);
      if (!raw) return [];
      var parsed = JSON.parse(raw);
      return sanitize(parsed);
    } catch (error) {
      /* Private mode / disabled storage / corrupt JSON. */
      storageAvailable = false;
      return memoryStore.slice();
    }
  }

  function writeStorage(list) {
    memoryStore = sanitize(list);

    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(memoryStore));
    } catch (error) {
      storageAvailable = false;
    }

    return memoryStore.slice();
  }

  function get() {
    return readStorage();
  }

  function has(handle) {
    if (typeof handle !== 'string' || handle === '') return false;
    return get().indexOf(handle.trim()) !== -1;
  }

  function count() {
    return get().length;
  }

  function toggle(handle) {
    if (typeof handle !== 'string' || handle.trim() === '') return false;

    var key = handle.trim();
    var list = get();
    var index = list.indexOf(key);
    var isActive;

    if (index === -1) {
      list.push(key);
      isActive = true;
    } else {
      list.splice(index, 1);
      isActive = false;
    }

    writeStorage(list);
    sync();
    emitChange();

    return isActive;
  }

  function emitChange() {
    var event;
    var detail = { count: count() };

    try {
      event = new CustomEvent('wishlist:change', { detail: detail, bubbles: true });
    } catch (error) {
      /* Legacy fallback. */
      event = document.createEvent('CustomEvent');
      event.initCustomEvent('wishlist:change', true, false, detail);
    }

    document.dispatchEvent(event);
  }

  /* Sync every heart and every counter in the document with storage. */
  function sync() {
    var list = get();
    var total = list.length;
    var buttons = document.querySelectorAll(TOGGLE_SELECTOR + '[data-product-handle]');
    var counters = document.querySelectorAll(COUNT_SELECTOR);
    var i;

    for (i = 0; i < buttons.length; i++) {
      var button = buttons[i];
      var handle = (button.getAttribute('data-product-handle') || '').trim();
      var active = handle !== '' && list.indexOf(handle) !== -1;

      button.setAttribute('aria-pressed', active ? 'true' : 'false');

      var addLabel = button.getAttribute('data-label-add');
      var removeLabel = button.getAttribute('data-label-remove');
      var label = active ? removeLabel : addLabel;

      if (label) {
        button.setAttribute('aria-label', label);
        if (button.hasAttribute('title')) button.setAttribute('title', label);
      }
    }

    for (i = 0; i < counters.length; i++) {
      var counter = counters[i];
      counter.textContent = String(total);
      counter.setAttribute('data-wishlist-count', String(total));

      /* Opt-in: <span data-wishlist-count data-wishlist-count-hide="empty"> */
      if (counter.getAttribute('data-wishlist-count-hide') === 'empty') {
        if (total > 0) {
          counter.removeAttribute('hidden');
        } else {
          counter.setAttribute('hidden', 'hidden');
        }
      }
    }
  }

  function onDocumentClick(event) {
    var target = event.target;
    if (!target || typeof target.closest !== 'function') return;

    var button = target.closest(TOGGLE_SELECTOR);
    if (!button) return;

    var handle = (button.getAttribute('data-product-handle') || '').trim();
    if (handle === '') return;

    /* The heart sits on top of the card link — never let the click navigate. */
    event.preventDefault();
    event.stopPropagation();

    toggle(handle);
  }

  window.Wishlist = {
    get: get,
    has: has,
    toggle: toggle,
    count: count,
    __initialized: true,
  };

  document.addEventListener('click', onDocumentClick);
  document.addEventListener('wishlist:change', sync);

  /* Theme editor re-renders a section: re-sync the hearts it just injected. */
  document.addEventListener('shopify:section:load', sync);

  /* Back/forward cache restore: storage may have changed in another tab. */
  window.addEventListener('pageshow', sync);

  /* Another tab changed the wishlist. */
  window.addEventListener('storage', function (event) {
    if (event && event.key && event.key !== STORAGE_KEY) return;
    sync();
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', sync);
  } else {
    sync();
  }
})();
